import { getApiBaseUrl, handleApiError } from '../../core/config/api';
import { User } from '../types';

export interface Role {
  id: number;
  role_name: string;
  description: string;
}

export interface UserWithRole extends User {
  role_name?: string;
  role_description?: string;
}

export interface UsersResponse {
  success: boolean;
  data: UserWithRole[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  error?: string;
}

export interface RoleHistory {
  id: number;
  old_role_id: number;
  new_role_id: number;
  old_role_name: string;
  new_role_name: string;
  reason: string | null;
  created_at: string;
  changed_by_username: string;
}

class UserService {
  private getAuthToken(): string | null {
    // Récupérer le token depuis le localStorage ou le store Redux
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    return token;
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  }

  /**
   * Récupère la liste des utilisateurs avec leurs rôles
   */
  async getUsers(params?: {
    search?: string;
    role?: number;
    page?: number;
    limit?: number;
  }): Promise<UsersResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.search) queryParams.append('search', params.search);
      if (params?.role) queryParams.append('role', params.role.toString());
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());

      const url = `${getApiBaseUrl(true)}/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Récupère les détails d'un utilisateur spécifique
   */
  async getUserById(id: number): Promise<{ success: boolean; data: UserWithRole; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/${id}`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Récupère la liste de tous les rôles disponibles
   */
  async getRoles(): Promise<{ success: boolean; data: Role[]; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/roles`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Met à jour le rôle d'un utilisateur
   */
  async updateUserRole(
    userId: number,
    rolesId: number,
    reason?: string
  ): Promise<{ success: boolean; message: string; data: UserWithRole; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/${userId}/role`;
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify({ roles_id: rolesId, reason }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Active ou désactive l'accès d'un utilisateur
   */
  async updateUserAccess(
    userId: number,
    activated: boolean,
    reason?: string
  ): Promise<{ success: boolean; message: string; data: UserWithRole; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/${userId}/access`;
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify({ activated, reason }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Récupère l'historique des changements de rôle d'un utilisateur
   */
  async getUserRoleHistory(userId: number): Promise<{
    success: boolean;
    data: RoleHistory[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/${userId}/role-history`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Récupère les accès aux pages du panel d'un utilisateur
   */
  async getUserPanelAccess(userId: number): Promise<{
    success: boolean;
    data: PanelAccess[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/${userId}/panel-access`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Met à jour l'accès à une section du panel pour un utilisateur
   */
  async updateUserPanelAccess(
    userId: number,
    section: string,
    hasAccess: boolean,
    reason?: string
  ): Promise<{
    success: boolean;
    message: string;
    data: PanelAccess;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/${userId}/panel-access/${section}`;
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify({ has_access: hasAccess, reason }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Récupère la liste des sections du panel disponibles
   */
  async getPanelSections(): Promise<{
    success: boolean;
    data: PanelSection[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/panel/sections`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Récupère les référents assignés à l'utilisateur connecté (pour le panel)
   */
  async getMyReferents(): Promise<{
    success: boolean;
    data: Referent[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/me/referents`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Récupère les référents assignés à un utilisateur
   */
  async getUserReferents(userId: number): Promise<{
    success: boolean;
    data: Referent[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/${userId}/referents`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Assigne une fonction de référent à un utilisateur
   */
  async assignReferent(
    userId: number,
    referentFunction: string,
    description?: string
  ): Promise<{
    success: boolean;
    message: string;
    data: Referent;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/${userId}/referents`;
      const response = await this.fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify({ function: referentFunction, description }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Retire une fonction de référent d'un utilisateur
   */
  async removeReferent(
    userId: number,
    referentFunction: string
  ): Promise<{
    success: boolean;
    message: string;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/${userId}/referents/${encodeURIComponent(referentFunction)}`;
      const response = await this.fetchWithAuth(url, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /** Types référent (table séparée) — CM / Admin */
  async getReferentTypes(): Promise<{
    success: boolean;
    data: ReferentTypeDefinition[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/types`;
      const response = await this.fetchWithAuth(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async createReferentType(body: {
    type_key: string;
    name: string;
    description?: string;
  }): Promise<{ success: boolean; message?: string; data: ReferentTypeDefinition; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/types`;
      const response = await this.fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async updateReferentType(
    id: number,
    body: Partial<Pick<ReferentTypeDefinition, 'type_key' | 'name' | 'description' | 'is_active'>>
  ): Promise<{ success: boolean; message?: string; data: ReferentTypeDefinition; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/types/${id}`;
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async deactivateReferentType(id: number): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/types/${id}`;
      const response = await this.fetchWithAuth(url, { method: 'DELETE' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Liste des définitions de fonctions référent (admin CM / Admin) — actives et inactives
   */
  async getReferentDefinitions(): Promise<{
    success: boolean;
    data: ReferentFunctionDefinition[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/definitions`;
      const response = await this.fetchWithAuth(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async createReferentDefinition(body: {
    function_key: string;
    name: string;
    description?: string;
    referent_type_id: number;
  }): Promise<{
    success: boolean;
    message?: string;
    data: ReferentFunctionDefinition;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/definitions`;
      const response = await this.fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async updateReferentDefinition(
    id: number,
    body: Partial<Pick<ReferentFunctionDefinition, 'name' | 'description' | 'referent_type_id' | 'is_active'>>
  ): Promise<{
    success: boolean;
    message?: string;
    data: ReferentFunctionDefinition;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/definitions/${id}`;
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async deactivateReferentDefinition(id: number): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/definitions/${id}`;
      const response = await this.fetchWithAuth(url, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Récupère la liste des fonctions disponibles pour les référents selon leur type
   */
  async getReferentFunctions(referentType?: string): Promise<{
    success: boolean;
    data: ReferentFunction[];
    error?: string;
  }> {
    try {
      const url = referentType 
        ? `${getApiBaseUrl(true)}/users/referents/functions?referent_type=${encodeURIComponent(referentType)}`
        : `${getApiBaseUrl(true)}/users/referents/functions`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /** Codes d'invitation visibles/générables dans le bloc Référent (CM/Admin). */
  async getInvitationCodes(): Promise<{
    success: boolean;
    data: InvitationCodeRow[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/invitation-codes`;
      const response = await this.fetchWithAuth(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async generateInvitationCodes(body?: {
    quantity?: number;
    expires_at?: string | null;
  }): Promise<{
    success: boolean;
    message?: string;
    data: InvitationCodeRow[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/referents/invitation-codes/generate`;
      const response = await this.fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify(body || {}),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Envoie un email de réinitialisation de mot de passe à un utilisateur
   */
  async resetUserPassword(userId: number): Promise<{
    success: boolean;
    message: string;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/${userId}/reset-password`;
      const response = await this.fetchWithAuth(url, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Récupère l'historique des adresses IP d'un utilisateur
   */
  async getUserIpHistory(userId: number): Promise<{
    success: boolean;
    data: UserIpHistory[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/${userId}/ip-history`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }


  /**
   * Récupère tous les utilisateurs actuellement en ligne
   */
  async getOnlineUsers(): Promise<{
    success: boolean;
    data: Array<{ user_id: number; username: string; email: string; last_activity: string; session_count: number }>;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/sessions/online`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Déconnecte de force un utilisateur (supprime toutes ses sessions)
   */
  async forceLogoutUser(userId: number): Promise<{
    success: boolean;
    message?: string;
    deletedCount?: number;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/sessions/force-logout/${userId}`;
      const response = await this.fetchWithAuth(url, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Récupère les accès par défaut d'un rôle
   */
  async getRoleDefaultAccess(roleId: number): Promise<{
    success: boolean;
    data: RoleDefaultAccess[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/roles/${roleId}/default-access`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Récupère tous les accès par défaut de tous les rôles
   */
  async getAllRolesDefaultAccess(): Promise<{
    success: boolean;
    data: RoleDefaultAccess[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/roles/default-access`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Met à jour l'accès par défaut à une section du panel pour un rôle
   */
  async updateRoleDefaultAccess(
    roleId: number,
    section: string,
    hasAccess: boolean
  ): Promise<{
    success: boolean;
    message: string;
    data: RoleDefaultAccess;
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/roles/${roleId}/default-access/${section}`;
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify({ has_access: hasAccess }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /** Tickets support internes équipe (rôles panel ≤ 5) */
  async getPanelTeamTickets(): Promise<{
    success: boolean;
    data: PanelTeamTicket[];
    error?: string;
  }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/panel-team-tickets`;
      const response = await this.fetchWithAuth(url);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async createPanelTeamTicket(body: {
    title: string;
    body: string;
    category?: string;
  }): Promise<{ success: boolean; message?: string; data: PanelTeamTicket; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/panel-team-tickets`;
      const response = await this.fetchWithAuth(url, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  async updatePanelTeamTicketStatus(
    id: number,
    status: 'open' | 'in_progress' | 'closed'
  ): Promise<{ success: boolean; message?: string; data: PanelTeamTicket; error?: string }> {
    try {
      const url = `${getApiBaseUrl(true)}/users/panel-team-tickets/${id}`;
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Réinitialise le blocage anti-brute-force (mémoire serveur) pour l'email du joueur.
   * Réservé aux administrateurs (API).
   */
  async clearAuthRateLimit(userId: number): Promise<{
    success: boolean;
    message?: string;
    cleared?: number;
    error?: string;
  }> {
    const response = await this.fetchWithAuth(`${getApiBaseUrl(true)}/users/clear-auth-rate-limit`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return { success: false, error: data.error || `Erreur HTTP ${response.status}` };
    }
    return data;
  }
}

export interface PanelTeamTicket {
  id: number;
  user_id: number;
  title: string;
  body: string;
  category: string;
  status: string;
  created_at: string;
  updated_at: string;
  author_username: string;
}

export interface UserIpHistory {
  id: number;
  user_id: number;
  ip_address: string;
  first_seen: string;
  last_seen: string;
  session_count?: number;
  user_agent?: string;
  is_trusted?: boolean;
  location?: string;
}

export interface PanelAccess {
  id: number;
  section: string;
  has_access: boolean;
  reason: string | null;
  created_at: string;
  updated_at: string;
  granted_by_username: string | null;
}

export interface PanelSection {
  id: string;
  name: string;
  description: string;
}

export interface Referent {
  id: number;
  user_id: number;
  function: string;
  referent_type: string;
  description: string | null;
  is_active: boolean;
  assigned_at: string;
  created_at: string;
  updated_at: string;
  assigned_by_username: string | null;
}

export interface ReferentFunction {
  id: string;
  name: string;
  description: string;
}

/** Type référent (table auth_referent_type). */
export interface ReferentTypeDefinition {
  id: number;
  type_key: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Définition d’une fonction référent, liée à un type (auth_referent_function). */
export interface ReferentFunctionDefinition {
  id: number;
  function_key: string;
  name: string;
  description: string | null;
  referent_type_id: number;
  type_key: string;
  type_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface InvitationCodeRow {
  id: number;
  code: string;
  used: number | boolean;
  used_by: number | null;
  used_by_username?: string | null;
  used_at: string | null;
  expires_at: string | null;
  created_at: string;
  created_by?: number | null;
  created_by_username?: string | null;
  notes?: string | null;
}

export interface RoleDefaultAccess {
  id: number;
  role_id: number;
  role_name?: string;
  section: string;
  has_access: boolean;
  created_at: string;
  updated_at: string;
  granted_by_username: string | null;
}

const userService = new UserService();
export default userService;

